import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  CalendarCheck, CheckCircle2, Clock, EyeOff, GripVertical, Layers, LayoutGrid, List, Package, Pencil, Plus, RefreshCw,
  Search, TriangleAlert, UtensilsCrossed, Wrench, X,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyCurrency, useLabels, useWorkspace } from '@/contexts/WorkspaceContext';
import { catalogService, CatalogItem, CatalogKind, CatalogSummary } from '@/services/catalog.service';
import CatalogItemEditor from './CatalogItemEditor';
import { Chip, formatMoney, Switch } from './shared';

type View = 'grid' | 'list';
type AvailFilter = 'all' | 'available' | 'unavailable';
const VIEW_KEY = 'slightor_catalog_view';
const UNCATEGORISED = '__none__';
const PAGE = 200;

export default function CatalogPage() {
  const t = useLabels();
  const { info } = useWorkspace();
  const { user } = useAuth();
  const companyCurrency = useCompanyCurrency();
  const isAdmin = user?.role === 'company_admin';
  // Only Food and Technology have catalog copy of their own; other families read as general.
  const familyKey = info?.family?.key;
  const family = familyKey === 'food' || familyKey === 'technology' ? familyKey : null;
  const plural = t('catalog');
  const noun = t('catalog', 'singular');

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [kind, setKind] = useState<CatalogKind | 'all'>('all');
  const [avail, setAvail] = useState<AvailFilter>('all');
  const [view, setView] = useState<View>(() => {
    try { return (localStorage.getItem(VIEW_KEY) as View) || 'grid'; } catch { return 'grid'; }
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dragUid, setDragUid] = useState<string | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(h);
  }, [query]);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* storage blocked */ }
  }, [view]);

  const filtered = !!debounced || category !== null || kind !== 'all' || avail !== 'all';

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    try {
      const [list, cats] = await Promise.all([
        catalogService.list({
          q: debounced || undefined,
          category: category === null ? undefined : category === UNCATEGORISED ? '' : category,
          kind: kind === 'all' ? undefined : kind,
          available: avail === 'all' ? undefined : avail === 'available' ? 'true' : 'false',
          limit: PAGE,
        }),
        catalogService.categories(),
      ]);
      if (id !== reqId.current) return;
      setItems(list.items);
      setTotal(list.total);
      setSummary(cats);
      setSelected((s) => new Set(Array.from(s).filter((uid) => list.items.some((i) => i.itemUid === uid))));
    } catch {
      if (id === reqId.current) setError(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [debounced, category, kind, avail]);

  useEffect(() => { void load(); }, [load]);

  const refreshSummary = () => catalogService.categories().then(setSummary).catch(() => { /* KPI strip keeps its last numbers */ });

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (it: CatalogItem) => { setEditing(it); setEditorOpen(true); };

  const toggleAvailability = async (it: CatalogItem, next: boolean) => {
    setItems((arr) => arr.map((x) => (x.itemUid === it.itemUid ? { ...x, isAvailable: next } : x)));
    try {
      await catalogService.setAvailability([it.itemUid], next);
      toast.success(next ? `${it.name} is available` : `${it.name} marked unavailable`, { id: `avail-${it.itemUid}` });
      void refreshSummary();
    } catch {
      setItems((arr) => arr.map((x) => (x.itemUid === it.itemUid ? { ...x, isAvailable: it.isAvailable } : x)));
    }
  };

  const bulkAvailability = async (next: boolean) => {
    const uids = Array.from(selected);
    if (!uids.length) return;
    setBulkBusy(true);
    try {
      const r = await catalogService.setAvailability(uids, next);
      toast.success(`${r.matched} ${r.matched === 1 ? noun.toLowerCase() : plural.toLowerCase()} ${next ? 'available' : 'marked unavailable'}`);
      setSelected(new Set());
      await load();
    } catch { /* interceptor toasts */ } finally { setBulkBusy(false); }
  };

  const canReorder = view === 'list' && !filtered && total <= PAGE && items.length > 1;
  const onDrop = async (targetUid: string) => {
    const from = items.findIndex((i) => i.itemUid === dragUid);
    const to = items.findIndex((i) => i.itemUid === targetUid);
    setDragUid(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const before = items;
    setItems(next.map((i, idx) => ({ ...i, sortOrder: idx })));
    try {
      await catalogService.reorder(next.map((i) => i.itemUid));
    } catch {
      setItems(before);
    }
  };

  const categoryNames = useMemo(() => (summary?.items ?? []).map((c) => c.category), [summary]);
  const emptyCatalog = !loading && !error && !filtered && (summary?.totals.items ?? items.length) === 0;
  const EmptyIcon = family === 'food' ? UtensilsCrossed : family === 'technology' ? Wrench : Package;

  const allSelected = items.length > 0 && items.every((i) => selected.has(i.itemUid));

  return (
    <>
      <Topbar
        title={plural}
        breadcrumb={`${t('operations')} / ${plural}`}
        description={family === 'food' ? 'Everything on your menu — prices, photos and what’s available today.' : `Everything you sell — prices, photos and what’s bookable.`}
        right={
          <div className="flex items-center gap-2">
            <div className="hidden rounded-lg border border-border bg-surface-alt p-0.5 sm:flex" role="radiogroup" aria-label="View">
              {(['grid', 'list'] as View[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={view === v}
                  aria-label={v === 'grid' ? 'Grid view' : 'List view'}
                  onClick={() => setView(v)}
                  className={clsx('flex h-8 w-8 items-center justify-center rounded-md transition-colors', view === v ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}
                >
                  {v === 'grid' ? <LayoutGrid size={15} /> : <List size={15} />}
                </button>
              ))}
            </div>
            <Button iconLeft={<Plus size={15} />} onClick={openNew} className="whitespace-nowrap">Add {noun.toLowerCase()}</Button>
          </div>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label={plural} value={summary?.totals.items} icon={<Layers size={16} />} tone="gold" />
          <Kpi label="Available" value={summary?.totals.available} icon={<CheckCircle2 size={16} />} tone="green"
            hint={summary && summary.totals.items > summary.totals.available ? `${summary.totals.items - summary.totals.available} unavailable` : undefined} />
          <Kpi label="Bookable" value={summary?.totals.bookable} icon={<CalendarCheck size={16} />} tone="blue" hint="On your booking link" />
          <Kpi label="Categories" value={summary?.totals.categories} icon={<LayoutGrid size={16} />} tone="amber" />
        </div>

        {emptyCatalog ? (
          <EmptyState
            icon={<EmptyIcon size={28} />}
            title={`Your ${plural.toLowerCase()} is empty`}
            description={family === 'food'
              ? 'Add your dishes and drinks with a photo and a price. They show up instantly when you take an order.'
              : `Add what you sell with a price. Services can be made bookable so customers pick a time on your booking link.`}
            action={<Button iconLeft={<Plus size={15} />} onClick={openNew}>Add your first {noun.toLowerCase()}</Button>}
          />
        ) : (
          <div className="rounded-2xl border border-border bg-surface shadow-soft">
            <div className="flex flex-col gap-3 border-b border-border p-4 lg:flex-row lg:items-center">
              <div className="w-full lg:max-w-xs">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${plural.toLowerCase()}…`}
                  iconLeft={<Search size={15} />}
                  iconRight={query ? <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="hover:text-text-primary"><X size={14} /></button> : undefined}
                  aria-label={`Search ${plural.toLowerCase()}`}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                <Chip active={kind === 'all'} onClick={() => setKind('all')}>All types</Chip>
                <Chip active={kind === 'product'} onClick={() => setKind('product')}><Package size={13} /> Products</Chip>
                <Chip active={kind === 'service'} onClick={() => setKind('service')}><Wrench size={13} /> Services</Chip>
                <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
                <Chip active={avail === 'available'} onClick={() => setAvail(avail === 'available' ? 'all' : 'available')}><CheckCircle2 size={13} /> Available</Chip>
                <Chip active={avail === 'unavailable'} onClick={() => setAvail(avail === 'unavailable' ? 'all' : 'unavailable')}><EyeOff size={13} /> Unavailable</Chip>
                <div className="flex rounded-lg border border-border bg-surface-alt p-0.5 sm:hidden">
                  {(['grid', 'list'] as View[]).map((v) => (
                    <button key={v} type="button" aria-label={v === 'grid' ? 'Grid view' : 'List view'} onClick={() => setView(v)}
                      className={clsx('flex h-7 w-7 items-center justify-center rounded-md', view === v ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted')}>
                      {v === 'grid' ? <LayoutGrid size={14} /> : <List size={14} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {(categoryNames.length > 0 || (summary?.uncategorised ?? 0) > 0) && (
              <div className="-mb-px flex gap-2 overflow-x-auto border-b border-border px-4 py-3 [scrollbar-width:none]">
                <Chip active={category === null} onClick={() => setCategory(null)} count={summary?.totals.items}>All</Chip>
                {(summary?.items ?? []).map((c) => (
                  <Chip key={c.category} active={category === c.category} onClick={() => setCategory(category === c.category ? null : c.category)} count={c.count}>
                    {c.category}
                  </Chip>
                ))}
                {(summary?.uncategorised ?? 0) > 0 && categoryNames.length > 0 && (
                  <Chip active={category === UNCATEGORISED} onClick={() => setCategory(category === UNCATEGORISED ? null : UNCATEGORISED)} count={summary?.uncategorised}>
                    Uncategorised
                  </Chip>
                )}
              </div>
            )}

            {view === 'list' && selected.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-primary-soft/50 px-4 py-2.5">
                <span className="text-[13px] font-medium text-text-primary">{selected.size} selected</span>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => void bulkAvailability(true)} iconLeft={<CheckCircle2 size={13} />}>Mark available</Button>
                  <Button size="sm" variant="secondary" loading={bulkBusy} onClick={() => void bulkAvailability(false)} iconLeft={<EyeOff size={13} />}>Mark unavailable</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
                </div>
              </div>
            )}

            <div className="p-4">
              {error ? (
                <div className="flex flex-col items-center gap-3 py-14 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger"><TriangleAlert size={22} /></span>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Couldn’t load your {plural.toLowerCase()}</p>
                    <p className="mt-1 text-xs text-text-muted">Check your connection and try again.</p>
                  </div>
                  <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={() => void load()}>Retry</Button>
                </div>
              ) : loading && items.length === 0 ? (
                view === 'grid' ? (
                  <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="overflow-hidden rounded-2xl border border-border">
                        <div className="skeleton aspect-[4/3]" />
                        <div className="space-y-2 p-3.5"><div className="skeleton h-4 w-2/3 rounded" /><div className="skeleton h-3 w-1/3 rounded" /></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
                )
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-14 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-alt text-text-muted"><Search size={20} /></span>
                  <p className="text-sm font-semibold text-text-primary">Nothing matches these filters</p>
                  <button
                    type="button"
                    className="text-xs font-semibold text-primary hover:underline"
                    onClick={() => { setQuery(''); setCategory(null); setKind('all'); setAvail('all'); }}
                  >
                    Clear filters
                  </button>
                </div>
              ) : view === 'grid' ? (
                <div className={clsx('grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 transition-opacity', loading && 'opacity-60')}>
                  {items.map((it) => (
                    <ItemCard key={it.itemUid} item={it} currency={it.currency ?? companyCurrency} family={family}
                      onOpen={() => openEdit(it)} onToggle={(next) => void toggleAvailability(it, next)} />
                  ))}
                </div>
              ) : (
                <div className={clsx('overflow-hidden rounded-xl border border-border transition-opacity', loading && 'opacity-60')}>
                  <div className="hidden grid-cols-[44px_minmax(0,1fr)_140px_120px_96px_40px] items-center gap-3 border-b border-border bg-surface-alt px-3 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      style={{ marginLeft: canReorder ? 19 : 0 }}
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.itemUid)))}
                      className="h-4 w-4 accent-[var(--color-primary)]"
                    />
                    <span>{noun}</span>
                    <span>Category</span>
                    <span className="text-right">Price</span>
                    <span className="text-center">Available</span>
                    <span />
                  </div>
                  <ul className="divide-y divide-border">
                    {items.map((it) => (
                      <li
                        key={it.itemUid}
                        draggable={canReorder}
                        onDragStart={() => setDragUid(it.itemUid)}
                        onDragOver={(e) => { if (canReorder) e.preventDefault(); }}
                        onDrop={() => void onDrop(it.itemUid)}
                        onDragEnd={() => setDragUid(null)}
                        className={clsx(
                          'grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 transition-colors hover:bg-surface-alt/60 md:grid-cols-[44px_minmax(0,1fr)_140px_120px_96px_40px]',
                          dragUid === it.itemUid && 'opacity-40',
                          !it.isAvailable && 'bg-surface-alt/40',
                        )}
                      >
                        <div className="flex items-center gap-1">
                          {canReorder && (
                            <span className="hidden cursor-grab text-text-muted md:block" title="Drag to reorder"><GripVertical size={15} /></span>
                          )}
                          <input
                            type="checkbox"
                            aria-label={`Select ${it.name}`}
                            checked={selected.has(it.itemUid)}
                            onChange={() => setSelected((s) => { const n = new Set(s); if (n.has(it.itemUid)) n.delete(it.itemUid); else n.add(it.itemUid); return n; })}
                            className="h-4 w-4 accent-[var(--color-primary)]"
                          />
                        </div>
                        <button type="button" onClick={() => openEdit(it)} className="flex min-w-0 items-center gap-3 text-left">
                          <Thumb item={it} family={family} className="h-11 w-11 rounded-lg" />
                          <span className="min-w-0">
                            <span className={clsx('flex items-center gap-1.5 truncate text-[13.5px] font-semibold', it.isAvailable ? 'text-text-primary' : 'text-text-muted')}>
                              <span className="truncate">{it.name}</span>
                              {it.bookable && <CalendarCheck size={13} className="shrink-0 text-info" aria-label="Bookable" />}
                            </span>
                            <span className="block truncate text-xs text-text-muted">
                              {[it.kind === 'service' && it.durationMinutes ? `${it.durationMinutes} min` : null, it.tags.slice(0, 3).join(' · ') || null, it.sku].filter(Boolean).join('  ·  ') || it.description || ' '}
                            </span>
                          </span>
                        </button>
                        <span className="hidden truncate text-[13px] text-text-secondary md:block">{it.category || <span className="text-text-muted">—</span>}</span>
                        <span className="text-right text-[13px] font-semibold tabular-nums text-text-primary md:block">
                          {it.price === null ? <span className="font-normal text-text-muted">On request</span> : it.price === 0 ? 'Free' : formatMoney(it.price, it.currency ?? companyCurrency)}
                        </span>
                        <span className="hidden justify-center md:flex">
                          <Switch checked={it.isAvailable} onChange={(next) => void toggleAvailability(it, next)} label={it.isAvailable ? 'Available' : 'Unavailable'} size="sm" />
                        </span>
                        <button type="button" onClick={() => openEdit(it)} aria-label={`Edit ${it.name}`} className="hidden h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary md:flex">
                          <Pencil size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!loading && !error && total > items.length && (
                <p className="mt-4 text-center text-xs text-text-muted">Showing the first {items.length} of {total}. Search or pick a category to narrow it down.</p>
              )}
              {canReorder && (
                <p className="mt-3 text-center text-2xs text-text-muted">Drag rows to change the order they appear in.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <CatalogItemEditor
        open={editorOpen}
        item={editing}
        categories={categoryNames}
        family={family}
        canDelete={isAdmin}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); void load(); }}
        onDeleted={() => { setEditorOpen(false); void load(); }}
      />
    </>
  );
}

const TONES = {
  gold: 'bg-primary-soft text-primary',
  green: 'bg-success-soft text-success',
  blue: 'bg-info-soft text-info',
  amber: 'bg-warning-soft text-warning',
} as const;

function Kpi({ label, value, icon, tone, hint }: { label: string; value?: number; icon: React.ReactNode; tone: keyof typeof TONES; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-soft">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</span>
        <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', TONES[tone])}>{icon}</span>
      </div>
      {value === undefined ? (
        <div className="skeleton mt-2 h-7 w-12 rounded" />
      ) : (
        <div className="mt-1 font-display text-[26px] font-bold leading-tight tracking-tight tabular-nums text-text-primary">{value}</div>
      )}
      <div className="mt-0.5 h-4 truncate text-2xs text-text-muted">{hint ?? ''}</div>
    </div>
  );
}

function Thumb({ item, family, className }: { item: CatalogItem; family: 'food' | 'technology' | null; className?: string }) {
  const Icon = item.kind === 'service' ? (item.bookable ? CalendarCheck : Wrench) : family === 'food' ? UtensilsCrossed : Package;
  if (item.imageUrl) {
    return <img src={item.imageUrl} alt="" loading="lazy" className={clsx('shrink-0 object-cover', !item.isAvailable && 'grayscale', className)} />;
  }
  return (
    <span className={clsx('flex shrink-0 items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 text-primary', className)}>
      <Icon size={18} strokeWidth={1.6} />
    </span>
  );
}

function ItemCard({ item, currency, family, onOpen, onToggle }: {
  item: CatalogItem;
  currency: string;
  family: 'food' | 'technology' | null;
  onOpen: () => void;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-surface transition-all hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-surface-alt">
        <Thumb item={item} family={family} className="h-full w-full transition-transform duration-500 group-hover:scale-[1.03]" />
        {!item.isAvailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35">
            <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">Unavailable</span>
          </div>
        )}
        <div className="absolute left-2.5 top-2.5 flex flex-wrap gap-1.5">
          {item.bookable && (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface/95 px-2 py-0.5 text-2xs font-semibold text-info shadow-soft backdrop-blur">
              <CalendarCheck size={11} /> Bookable
            </span>
          )}
        </div>
        <div className="absolute right-2.5 top-2.5 rounded-full bg-surface/95 p-1 shadow-soft backdrop-blur" onClick={(e) => e.stopPropagation()}>
          <Switch checked={item.isAvailable} onChange={onToggle} size="sm" label={item.isAvailable ? 'Available — tap to mark unavailable' : 'Unavailable — tap to make available'} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={clsx('truncate text-[14px] font-semibold', item.isAvailable ? 'text-text-primary' : 'text-text-muted')}>{item.name}</div>
            <div className="truncate text-xs text-text-muted">{item.category || (item.kind === 'service' ? 'Service' : 'Uncategorised')}</div>
          </div>
          <div className="shrink-0 text-right text-[14px] font-bold tabular-nums text-text-primary">
            {item.price === null ? <span className="text-xs font-medium text-text-muted">On request</span> : item.price === 0 ? 'Free' : formatMoney(item.price, currency)}
          </div>
        </div>
        {item.description && <p className="line-clamp-2 text-xs leading-relaxed text-text-secondary">{item.description}</p>}
        {(item.tags.length > 0 || (item.kind === 'service' && item.durationMinutes)) && (
          <div className="mt-auto flex flex-wrap gap-1 pt-1">
            {item.kind === 'service' && item.durationMinutes ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-medium text-text-secondary"><Clock size={10} /> {item.durationMinutes} min</span>
            ) : null}
            {item.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-medium text-primary">{tag}</span>
            ))}
            {item.tags.length > 4 && <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">+{item.tags.length - 4}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
